function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// ----------------------------------------------------------------------------
// Product Gallery Module: Feature Slider
// ----------------------------------------------------------------------------
//
// Manages the product image gallery slider (mobile PDP carousel + optional thumbs).
// Swiper roots are scoped to each iam-product-gallery instance.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import Swiper from 'swiper';
import { Navigation, Thumbs, Pagination } from 'swiper/modules';
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import { toggleInnerFocus } from 'scripts/helpers/utilities';
import Debug from 'scripts/libs/debug';

// Styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/free-mode';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import 'swiper/css/thumbs';

// -------------------------------------
// Feature Slider
// -------------------------------------

export class FeatureSlider {
  constructor(iamProductGallery) {
    _defineProperty(this, "FOCUSABLE_ELEMENTS", ['iframe', 'input', 'select', 'option', 'textarea', 'button', 'video', 'label', 'a', '.plyr', 'model-viewer']);
    this.iamProductGallery = iamProductGallery;
    this._onVariantChange = this.onVariantChange.bind(this);
    this._variantSubscribed = false;

    // PDP metafield gallery has no [data-product-single-thumbnail]; variant media
    // is updated via Section Rendering on iam-product-gallery instead.
    if (this.iamProductGallery.querySelector('[data-product-single-thumbnail]')) {
      iamotaEventHandler.subscribe('productForm:variant:changed', this._onVariantChange, {
        context: this.iamProductGallery.iamForm,
        source: "featureSlider.js"
      });
      this._variantSubscribed = true;
    }
    this.thumbnailSlider = this.initThumbnailSlider();
    this.initFeatureSlider(this.thumbnailSlider);
    if (this.thumbnailSlider) {
      this.onThumbnailFocus();
    }
  }

  /**
   * On Slide Change
   */
  onSlideChange(swiper) {
    iamotaEventHandler.trigger('productForm:gallery:featureSlider:changed', {
      swiper
    }, {
      context: this.iamProductGallery
    });
    toggleInnerFocus(swiper, this.FOCUSABLE_ELEMENTS);
  }
  onThumbnailFocus() {
    if (!this.thumbnailSlider) return;
    this.thumbnailSlider.slides.forEach((slide, index) => {
      slide.addEventListener('focus', () => {
        this.featureSlider.slideTo(index);
      });
    });
  }

  /**
   * On Variant Change
   */
  onVariantChange(_ref) {
    var variant = _ref.variant;
    this.focusSlideOnSelection(variant);
  }

  /**
   * Tear down Swiper instances and event subscriptions (e.g. before AJAX gallery replace).
   */
  destroy() {
    if (this._variantSubscribed) {
      iamotaEventHandler.unsubscribe('productForm:variant:changed', this._onVariantChange);
      this._variantSubscribed = false;
    }
    if (this.featureSlider && typeof this.featureSlider.destroy === 'function') {
      this.featureSlider.destroy(true, true);
    }
    this.featureSlider = null;
    if (this.thumbnailSlider && typeof this.thumbnailSlider.destroy === 'function') {
      this.thumbnailSlider.destroy(true, true);
    }
    this.thumbnailSlider = null;
    if (this.iamProductGallery.featureSlider === this) {
      this.iamProductGallery.featureSlider = null;
    }
  }

  /**
   * Init the thumbnail slider
   */
  initThumbnailSlider() {
    var thumbnailEl = this.iamProductGallery.querySelector('.carousel--product-thumbnails');
    if (!thumbnailEl) return null;
    return new Swiper(thumbnailEl, {
      modules: [Navigation],
      slidesPerView: 4,
      spaceBetween: 8,
      freeMode: true,
      watchSlidesVisibility: true,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      navigation: true,
      a11y: false,
      multipleActiveThumbs: false,
      keyboard: {
        enabled: false
      },
      breakpoints: {
        768: {
          direction: 'vertical',
          slidesPerView: 6,
          spaceBetween: 10
        }
      }
    });
  }

  /**
   * Init the feature slider
   */
  initFeatureSlider(thumbnailSlider) {
    var el = this.iamProductGallery.querySelector('.carousel--featured-product');
    if (!el) return null;
    var paginationEl = this.iamProductGallery.querySelector('.swiper-pagination');
    var nextEl = this.iamProductGallery.querySelector('.swiper-button-next-product');
    var prevEl = this.iamProductGallery.querySelector('.swiper-button-prev-product');
    var nav = {};
    if (nextEl) nav.nextEl = nextEl;
    if (prevEl) nav.prevEl = prevEl;
    var config = {
      modules: [Navigation, Thumbs, Pagination],
      spaceBetween: 0,
      updateOnImagesReady: true,
      navigation: Object.keys(nav).length ? nav : false,
      on: {
        init: function (swiper) {
          Debug.extreme('productForm:gallery:featureSlider:init', swiper);
          this.featureSlider = swiper;
          this.onFeatureSliderReady(swiper);
          toggleInnerFocus(swiper, this.FOCUSABLE_ELEMENTS);
        }.bind(this),
        slideChange: function (swiper) {
          this.onSlideChange(swiper);
        }.bind(this)
      }
    };
    if (paginationEl) {
      config.pagination = {
        el: paginationEl,
        type: 'bullets',
        clickable: true
      };
    }
    if (thumbnailSlider) {
      config.thumbs = {
        swiper: thumbnailSlider
      };
    }
    return new Swiper(el, config);
  }
  onFeatureSliderReady(swiper) {
    Debug.extreme('productForm:gallery:onFeatureSliderReady', swiper);
    iamotaEventHandler.trigger('productForm:gallery:featureSlider:ready', {
      swiper
    }, {
      context: this.iamProductGallery
    });
    var hasThumbnails = this.iamProductGallery.querySelector('[data-product-single-thumbnail]');
    if (hasThumbnails) {
      this.focusSlideOnSelection(this.iamProductGallery.iamForm.getCurrentVariant());
    }
    this.iamProductGallery.setLoading(false);
  }
  focusSlideOnSelection(variant) {
    Debug.extreme('productForm:gallery:focusSlideOnSelection', variant);
    if (variant !== null && variant !== void 0 && variant.featured_media) {
      this.focusSlideOnMedia(variant.featured_media.id);
    } else {
      this.focusSlideOnNearestMatch();
    }
  }
  focusSlideOnMedia(mediaId) {
    Debug.extreme('productForm:gallery:focusSlideOnSelection', mediaId);
    var newImageId = "".concat(this.iamProductGallery.sectionId, "-").concat(mediaId);
    var thumbnail = this.iamProductGallery.querySelector("[data-product-single-thumbnail][data-thumbnail-id='".concat(newImageId, "']"));
    if (thumbnail && this.featureSlider) {
      var _thumbnail$parentElem;
      var slideIndex = parseInt((_thumbnail$parentElem = thumbnail.parentElement) === null || _thumbnail$parentElem === void 0 ? void 0 : _thumbnail$parentElem.getAttribute('data-index'), 10);
      if (!isNaN(slideIndex)) {
        this.featureSlider.slideTo(slideIndex);
      }
    }
  }
  focusSlideOnNearestMatch() {
    Debug.extreme('productForm:gallery:focusSlideOnNearestMatch');
    var options = this.iamProductGallery.iamForm.getCurrentOptions();
    for (var i = options.length - 1; i > 0; i--) {
      var partialOptions = options.slice(0, i);
      var closestMatch = this.findVariantWithOptions(partialOptions);
      if (closestMatch && closestMatch.featured_media) {
        this.focusSlideOnMedia(closestMatch.featured_media.id);
        return;
      }
    }
  }

  /**
   * Find a variant that matches the given options
   * @returns {variantObject}
   */
  findVariantWithOptions(options) {
    return this.iamProductGallery.iamForm.productData.variants.find(variant => options.every((option, index) => variant.options[index] === option.value));
  }
}
iamotaEventHandler.subscribe('productForm:gallery:init', _ref2 => {
  var iamProductGallery = _ref2.iamProductGallery;
  iamProductGallery.featureSlider = new FeatureSlider(iamProductGallery);
}, {
  source: "featureSlider.js"
});